import findIndex from 'lodash/findIndex';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IMAGE_SOURCE_PLACEHOLDER } from 'commons/containers/MediaLibrary';
import { useErrorContext } from 'commons/containers/ValidationErrorManager';
import { BuiltInComponents } from 'craft/constants/schema';
import { getDefaultAction } from 'craft/containers/Actions/getDefaultAction';
import { INTERACTION_TYPES } from 'craft/types/actions';
import { CollectionTargetLocation, ContentCollection } from 'craft/types/contentDocument';

import { FieldEditorToolbar } from './FieldEditorToolbar';
import { FieldEditorToolbox } from './FieldEditorToolbox';
import { FieldEditorProps } from './types';

const FIELD_NO_LONGER_AVAILABLE_ERROR = 'A Field used in this document is no longer available.';

export const FieldEditor = React.memo(
  ({
    context,
    nodeIds,
    type,
    collections,
    usedCollection,
    targetLocation,
    setCustom,
    setProps,
    collectionEmbedLocations,
  }: FieldEditorProps) => {
    const [selectedField, setSelectedField] = useState<ContentCollection | null>(null);
    const [isUsingCollectionField, setIsUsingCollectionField] = useState(false);

    useEffect(() => {
      const matchField = collections?.find(
        (contentSource: ContentCollection) =>
          contentSource.collectionId === usedCollection?.id &&
          contentSource.targetLocation === targetLocation,
      );
      if (matchField) {
        setSelectedField(matchField);
        setIsUsingCollectionField(true);
      } else {
        setSelectedField(null);
        setIsUsingCollectionField(false);
      }
    }, [collections, usedCollection, targetLocation]);

    const onToggleCheckbox = useCallback(() => {
      const newChecboxState = !isUsingCollectionField;
      setIsUsingCollectionField(newChecboxState);

      if (!newChecboxState) {
        const collectionIndex = findIndex(collections, {
          collectionId: usedCollection?.id,
          targetLocation,
        });
        setCustom({
          collections: collections?.filter(
            (_: unknown, index: number) => index !== collectionIndex,
          ),
        });
        if (type === BuiltInComponents.Image) {
          setProps({ src: '' });
        }
        if (targetLocation === CollectionTargetLocation.ActionURL) {
          setCustom({ actions: undefined });
        }
      }
    }, [isUsingCollectionField, collections, setProps, setCustom, usedCollection, targetLocation]);

    const onFieldSelection = useCallback(
      (contentCollections: ContentCollection) => {
        if (selectedField) {
          setSelectedField({ ...selectedField, ...contentCollections });
        }

        // if we select the none option, remove the collectionId entry from the node
        if (!contentCollections.fieldId) {
          const collectionIndex = findIndex(collections, {
            collectionId: usedCollection?.id,
            targetLocation,
          });
          setCustom({
            collections: collections?.filter(
              (_: unknown, index: number) => index !== collectionIndex,
            ),
          });
          return;
        }

        const index = findIndex(collections, {
          collectionId: selectedField?.collectionId,
          targetLocation,
        });

        if (index > -1) {
          const updatedCollections = [...collections];
          updatedCollections[index] = { ...contentCollections };
          setCustom({ collections: updatedCollections });
        } else {
          setCustom({
            collections: [...(collections || []), { ...contentCollections }],
          });
        }

        if (type === BuiltInComponents.Image) {
          setProps({ src: IMAGE_SOURCE_PLACEHOLDER });
        }
        if (targetLocation === CollectionTargetLocation.ActionURL) {
          setCustom({ actions: [getDefaultAction(INTERACTION_TYPES.URL, 'Link')] });
        }
      },
      [selectedField, collections, usedCollection, targetLocation, setCustom, type, setProps],
    );

    const { errors } = useErrorContext();

    const errorMessage: string | undefined = useMemo(() => {
      let message: string | undefined;

      if (nodeIds.length === 1) {
        message = errors.find((error) => {
          return error.nodeId === nodeIds[0] && error.details?.fieldId === selectedField?.fieldId;
        })?.message;

        if (message === FIELD_NO_LONGER_AVAILABLE_ERROR) {
          message = 'This field is not available';
        }
      }

      return message;
    }, [errors, nodeIds]);

    if (context?.isPopover) {
      return (
        <FieldEditorToolbar
          isUsingCollectionField={isUsingCollectionField}
          onToggleCheckbox={onToggleCheckbox}
          targetLocation={targetLocation}
          usedCollection={usedCollection}
          errorMessage={errorMessage}
          onFieldSelection={onFieldSelection}
          selectedField={selectedField}
          collectionEmbedLocations={collectionEmbedLocations}
        />
      );
    }
    return (
      <FieldEditorToolbox
        isUsingCollectionField={isUsingCollectionField}
        onToggleCheckbox={onToggleCheckbox}
        targetLocation={targetLocation}
        usedCollection={usedCollection}
        errorMessage={errorMessage}
        onFieldSelection={onFieldSelection}
        selectedField={selectedField}
        collectionEmbedLocations={collectionEmbedLocations}
      />
    );
  },
);
